import React from 'react';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { Card } from '@/components/ui/cards';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { RadioButtonGroup } from '@/components/ui/inputs/RadioButtonGroup';
import { Switch } from '@/components/ui/inputs';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import Table from '@/components/ui/tables/Table';
import { TABLE_ROW_FONT_SIZE } from '@/components/ui/tables/Table.constants';
import { useTranslation } from '@/i18n';
import { useMarksManager } from '@/hooks/attendance/marksManager';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';
import { MarksManagerQuickFilters, MarksManagerAdvancedFiltersToggle } from '@/components/features/attendance';


export const MarksManager: React.FC = () => {
  const { t: translate } = useTranslation();
  const marksManager = useMarksManager();

  return (
    <PageLayout dynamicTitle={translate('attendance:marksManager.title')}>
      <div className="flex flex-col gap-6" data-testid="marks-manager-page">
        {/* Card 1: tipo de consulta + switch de horas decimales (ASIS-5568) + rango de fecha */}
        <Card>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center justify-between gap-4">
              <RadioButtonGroup
                name="marksManagerQueryMode"
                value={marksManager.queryMode}
                onChange={(value) => marksManager.setQueryMode(value as 'rango' | 'corteMensual')}
                options={[
                  { value: 'rango', label: translate('attendance:marksManager.queryMode.range') },
                  {
                    value: 'corteMensual',
                    label: translate('attendance:marksManager.queryMode.monthlyCut'),
                    disabled: true,
                  },
                ]}
              />
              <Switch
                checked={marksManager.isDecimalFormat}
                onChange={marksManager.onHourFormatToggle}
                label={translate('attendance:marksManager.decimalSwitch.label')}
                tooltipText={translate('attendance:marksManager.decimalSwitch.tooltip')}
                data-testid="marks-manager-decimal-switch"
              />
            </div>
            <DateRangeFilter
              value={marksManager.dateRange}
              onChange={marksManager.setDateRange}
              minDate={getMinDateByMonths()}
              maxDate={formatDateToYYYYMMDD(new Date())}
            />
            <MarksManagerAdvancedFiltersToggle
              dimensionalFilter={marksManager.dimensionalFilter}
              onSearch={marksManager.onAdvancedSearch}
            />
          </div>
        </Card>

        {/* Card 2: filtros rápidos "Colaborador" / "Estado asistencia" (ASIS-5569) y acción de
            refresco */}
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4 p-4">
            <MarksManagerQuickFilters
              dimensionalFilter={marksManager.dimensionalFilter}
              attendanceStatusCodes={marksManager.attendanceStatusCodes}
              onAttendanceStatusCodesChange={marksManager.setAttendanceStatusCodes}
            />
            <div className="flex items-center gap-3">
              <PermissionGuard action="edit">
                <PrimaryButton
                  onClick={marksManager.onSaveRecalculate}
                  isLoading={marksManager.isRecalculating}
                  data-testid="marks-manager-save-recalculate-button"
                >
                  {translate('attendance:marksManager.actions.saveRecalculate')}
                </PrimaryButton>
              </PermissionGuard>
              <PrimaryButton
                variant="outline"
                onClick={marksManager.onRefresh}
                iconRight={<IconScout name="sync" />}
                data-testid="marks-manager-refresh-button"
              >
                {translate('attendance:marksManager.actions.refresh')}
              </PrimaryButton>
            </div>
          </div>
        </Card>

        {/* Card 3: tabla de resultados */}
        <Table
          key={marksManager.tableSelectionKey}
          columns={marksManager.columns}
          data={marksManager.rows}
          totalRows={marksManager.total}
          isLoading={marksManager.isLoading}
          emptyMessage={translate('attendance:marksManager.messages.noData')}
          searchable
          onSearch={marksManager.onSearch}
          serverSideSort
          initialSort={{ key: 'fullName', direction: 'asc' }}
          onSortChange={marksManager.onSortChange}
          selectable
          isSelectAllTotalActive={marksManager.isSelectAllTotalActive}
          onSelectAllTotal={marksManager.onSelectAllTotal}
          onClearSelectionTotal={marksManager.onClearSelectionTotal}
          fontSize={TABLE_ROW_FONT_SIZE}
          pagination={{
            page: marksManager.page,
            pageSize: marksManager.pageSize,
            total: marksManager.total,
            onChange: marksManager.setPage,
            onPageSizeChange: marksManager.setPageSize,
          }}
          data-testid="marks-manager-table"
        />
      </div>
    </PageLayout>
  );
};

export default MarksManager;
